import React from "react";
import { withRouter } from "react-router-dom";
import {
  LoginPageStyled,
  BlockLoginStyled,
  KeyButtonStyledBack,
  KeyButtonStyledNext,
  ButtonsGroupStyled,
  HeadingStyled,
  ContainerStyled,
  TextStyled,
  ProfileImage,
  ImageAndInputBlockStyled,
  InputContainerStyled,
  InputStyled,
  LogoStyled,
  ProfileImageContainer
} from "./styles/Styled";
import { KeyboardSwap, withFocusable } from "../../services/visualLibraryService";
import { withAppState } from "../../state";
import { i18n } from "../../services/i18nService";
import { withSubtheme } from "../../styles/themes/withSubtheme";
import { redirectTo } from "../../services/navigationService";
import { historyMethods } from "../../utils/historyMethods";
import { routes } from "../../routes";
import FreeLogo from "../../components/FreeLogo";
import { verifyPassword } from "../../services/multipleProfilesService";
import { getGlobalButtonInfo, getGlobalButtonFocusPath } from "../../errors/globalButtons";
import { config } from "../../config/config";
import { trackEvent } from "../../services/analyticsService";

const KeyButtonStyledBackFocus = withFocusable(KeyButtonStyledBack);
const KeyButtonStyledNextFocus = withFocusable(KeyButtonStyledNext);
const MP = "MP";

class AccountPassword extends React.Component {
  constructor(props) {
    super(props);
    this.userInput = React.createRef();
    this.state = {
      passwordInputValue: "",
      avatar: "",
      isShowPassword: false
    };
    this.updateValue = this.updateValue.bind(this);
    this.keyboardDidSwap = this.keyboardDidSwap.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.onClickContinue = this.onClickContinue.bind(this);
    this.onChangeShowPassword = this.onChangeShowPassword.bind(this);
    this.showWrongPasswordError = this.showWrongPasswordError.bind(this);
    this.onBack = this.onBack.bind(this);
    this.mounted = false;
  }

  componentDidMount() {
    const { onRef } = this.props;
    onRef && onRef(this);
    this.mounted = true;
    this.setProfileData();
  }

  async setProfileData() {
    const { history } = this.props;
    const hasHistoryState = !!(history.location && history.location.state);
    const avatar = history.location && history.location.state && history.location.state.avatar;
    if (hasHistoryState) {
      this.setState({
        avatar: avatar
      });
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      nextProps.currentFocus !== this.props.currentFocus ||
      nextState.passwordInputValue !== this.state.passwordInputValue ||
      nextState.isShowPassword !== this.state.isShowPassword
    );
  }

  closeModal() {
    this.setState({ passwordInputValue: "" });
    this.props.stateActions.hideGlobalModal();
    this.props.setFocus(KeyboardSwap.defaultFocusPath);
  }

  updateValue(value) {
    this.setState({ passwordInputValue: value });
  }

  checkLoginButtonDisability() {
    return this.state.passwordInputValue.length < config.get().minPassLength;
  }

  async onClickContinue() {
    const { history } = this.props;
    const password = this.state.passwordInputValue;
    const userId = localStorage.getItem("globalUserId");
    const response = await verifyPassword({ userId, password });
    const profileData = history.location.state.profileData;
    if (response.status === 200) {
      const { history } = this.props;
      const hasHistoryState = !!(history.location && history.location.state);
      if (hasHistoryState && history.location.state.backDestination) {
        const backDestination = history.location.state.backDestination;
        const nextPage = history.location.state.nextPage;
        const profileData = history.location.state.profileData;
        const oldProfileData = history.location.state.oldProfileData;
        const backDestinationPathname = backDestination.pathname || backDestination;
        const createPin = history.location.state.createPin || false;
        const popup = history.location.state.popup || false;
        trackEvent("account_password_success", null, MP, profileData);
        history.replace({
          pathname: nextPage || backDestinationPathname,
          state: {
            profileData: { ...profileData },
            oldProfileData: oldProfileData,
            isPasswordVerified: true,
            accountPassword: this.state.passwordInputValue,
            createPin: createPin,
            backDestination: routes.settings,
            profileId: profileData?.profileId,
            popup: popup,
            profileName: profileData?.profileName
          }
        });
        return true;
      }
    } else {
      trackEvent("account_password_failed", null, MP, profileData);
      await this.showWrongPasswordError();
    }
  }

  async showWrongPasswordError() {
    this.props.stateActions.showGlobalModal({
      modalTitle: i18n.get("pages.accountPassword.error.incorrectPassword"),
      modalText: i18n.get("pages.accountPassword.error.forgetPassword"),
      buttons: [
        {
          ...getGlobalButtonInfo("retryAccountPassword"),
          buttonHandler: this.closeModal
        }
      ]
    });

    this.props.setFocus(getGlobalButtonFocusPath("retryAccountPassword"));
  }

  keyboardDidSwap({ defaultFocusPath }) {
    this.props.setFocus(defaultFocusPath);
  }

  onBack() {
    const { history } = this.props;
    const hasHistoryState = !!(history.location && history.location.state);
    if (hasHistoryState && history.location.state.backDestination) {
      const backDestination = history.location.state.backDestination;
      const profileData = history.location.state.profileData;
      const oldProfileData = history.location.state.oldProfileData;
      const backDestinationPathname = backDestination.pathname || backDestination;
      trackEvent("account_password_cancel_click", null, MP, profileData);
      history.replace({
        pathname: backDestinationPathname,
        state: { profileData: { ...profileData }, oldProfileData, profileName: profileData?.profileName }
      });
      return true;
    }
    redirectTo.call(this, routes.home, null, historyMethods.REPLACE);
    return true;
  }

  onChangeShowPassword() {
    this.setState({ isShowPassword: !this.state.isShowPassword });
  }

  render() {
    return (
      <LoginPageStyled>
        <LogoStyled>
          <FreeLogo height={"1.968rem"} />
        </LogoStyled>
        <HeadingStyled rank={2} text={i18n.get("pages.accountPassword.accountPassowrdHeader")} />
        <TextStyled text={i18n.get("pages.accountPassword.accountPassowrdSubText")} />
        <ContainerStyled>
          <BlockLoginStyled>
            <KeyboardSwap
              text={this.state.passwordInputValue}
              onChange={this.updateValue}
              direction={i18n.getDirection()}
              onSwap={this.keyboardDidSwap}
              dataSnUp={""}
              showEmailKeys={false}
            />
            <ButtonsGroupStyled>
              <KeyButtonStyledBackFocus
                onClick={this.onBack}
                focusPath={"loginBackButton"}
                dataSnLeft={i18n.getDirection() === "ltr" ? "" : "@loginNextButton"}
                label={i18n.get("global.back")}
              ></KeyButtonStyledBackFocus>

              <KeyButtonStyledNextFocus
                onClick={this.onClickContinue}
                focusPath={"loginNextButton"}
                disabled={this.checkLoginButtonDisability()}
                dataSnLeft={i18n.getDirection() === "rtl" ? "" : "@loginBackButton"}
                label={i18n.get("global.continue")}
              ></KeyButtonStyledNextFocus>
            </ButtonsGroupStyled>
            <ButtonsGroupStyled>
              <KeyButtonStyledBackFocus
                onClick={this.onChangeShowPassword}
                focusPath={"showPasswordButton"}
                dataSnLeft={""}
                label={
                  this.state.isShowPassword
                    ? i18n.get("global.credentials.hidePassword")
                    : i18n.get("global.credentials.showPassword")
                }
              ></KeyButtonStyledBackFocus>
            </ButtonsGroupStyled>
          </BlockLoginStyled>
          <BlockLoginStyled>
            <ImageAndInputBlockStyled>
              <ProfileImageContainer>
                <ProfileImage src={this.state.avatar} />
              </ProfileImageContainer>
              <InputContainerStyled>
                <InputStyled
                  id={"profiler-name"}
                  type={this.state.isShowPassword ? "text" : "password"}
                  ref={this.userInput}
                  value={this.state.passwordInputValue}
                  placeholder={i18n.get("pages.accountPassword.accountPasswordInputPlaceholder")}
                  readOnly
                />
              </InputContainerStyled>
            </ImageAndInputBlockStyled>
          </BlockLoginStyled>
        </ContainerStyled>
        <TextStyled text={i18n.get("pages.accountPassword.forgetPassword")} />
      </LoginPageStyled>
    );
  }
}

const ProfileNamePage = withRouter(
  withAppState(
    withSubtheme(({ history, applicationState, stateActions, setFocus, onRef }) => (
      <AccountPassword
        history={history}
        currentFocus={applicationState.currentFocus}
        stateActions={stateActions}
        setFocus={setFocus}
        voucherData={applicationState.voucherData}
        signupUsername={applicationState.signupUsername}
        onRef={onRef}
      />
    ))
  )
);

ProfileNamePage.defaultFocusPathLTR = KeyboardSwap.defaultFocusPathLTR;
ProfileNamePage.defaultFocusPathRTL = KeyboardSwap.defaultFocusPathRTL;
ProfileNamePage.defaultFocusPath = KeyboardSwap.defaultFocusPath;

export default ProfileNamePage;
